import type { Localized } from '../types';

type PrivacyContent = {
  readonly title: string;
  readonly lead: string;
  readonly art1: { readonly title: string; readonly body: string };
  readonly art2: { readonly title: string; readonly body: string };
  readonly art3: {
    readonly title: string;
    readonly intro: string;
    readonly list: readonly string[];
  };
  readonly art4: { readonly title: string; readonly items: readonly string[] };
  readonly art5: {
    readonly title: string;
    readonly item1: {
      readonly intro: string;
      readonly sub: readonly string[];
      readonly lastSubIntro: string;
      readonly lastSub: readonly string[];
    };
    readonly item2: { readonly intro: string; readonly sub: readonly string[] };
  };
  readonly art6: {
    readonly title: string;
    readonly item1: { readonly intro: string; readonly sub: readonly string[] };
    readonly item2: string;
  };
  readonly art7: { readonly title: string; readonly items: readonly string[] };
  readonly art8: { readonly title: string; readonly items: readonly string[] };
  readonly art9: { readonly title: string; readonly items: readonly string[] };
  readonly art10: {
    readonly title: string;
    readonly intro: string;
    readonly address1: string;
    readonly address2: string;
    readonly company: string;
    readonly rep: string;
    readonly emailLabel: string;
  };
};

export const privacyDict: Localized<PrivacyContent> = {
  ja: {
    title: 'プライバシーポリシー',
    lead: 'CJインベストメント株式会社（以下，「当社」といいます。）は，本ウェブサイト上で提供するサービス（以下,「本サービス」といいます。）における，ユーザーの個人情報の取扱いについて，以下のとおりプライバシーポリシー（以下，「本ポリシー」といいます。）を定めます。',
    art1: {
      title: '第1条（個人情報）',
      body: '「個人情報」とは，個人情報保護法にいう「個人情報」を指すものとし，生存する個人に関する情報であって，当該情報に含まれる氏名，生年月日，住所，電話番号，連絡先その他の記述等により特定の個人を識別できる情報及び容貌，指紋，声紋にかかるデータ，及び健康保険証の保険者番号などの当該情報単体から特定の個人を識別できる情報（個人識別情報）を指します。',
    },
    art2: {
      title: '第2条（個人情報の収集方法）',
      body: '当社は，ユーザーが利用登録をする際に氏名，生年月日，住所，電話番号，メールアドレス，銀行口座番号，クレジットカード番号，運転免許証番号などの個人情報をお尋ねすることがあります。また，ユーザーと提携先などとの間でなされたユーザーの個人情報を含む取引記録や決済に関する情報を,当社の提携先（情報提供元，広告主，広告配信先などを含みます。以下，｢提携先｣といいます。）などから収集することがあります。',
    },
    art3: {
      title: '第3条（個人情報を収集・利用する目的）',
      intro: '当社が個人情報を収集・利用する目的は，以下のとおりです。',
      list: [
        '当社サービスの提供・運営のため',
        'ユーザーからのお問い合わせに回答するため（本人確認を行うことを含む）',
        'ユーザーが利用中のサービスの新機能，更新情報，キャンペーン等及び当社が提供する他のサービスの案内のメールを送付するため',
        'メンテナンス，重要なお知らせなど必要に応じたご連絡のため',
        '利用規約に違反したユーザーや，不正・不当な目的でサービスを利用しようとするユーザーの特定をし，ご利用をお断りするため',
        'ユーザーにご自身の登録情報の閲覧や変更，削除，ご利用状況の閲覧を行っていただくため',
        '有料サービスにおいて，ユーザーに利用料金を請求するため',
        '上記の利用目的に付随する目的',
      ],
    },
    art4: {
      title: '第4条（利用目的の変更）',
      items: [
        '当社は，利用目的が変更前と関連性を有すると合理的に認められる場合に限り，個人情報の利用目的を変更するものとします。',
        '利用目的の変更を行った場合には，変更後の目的について，当社所定の方法により，ユーザーに通知し，または本ウェブサイト上に公表するものとします。',
      ],
    },
    art5: {
      title: '第5条（個人情報の第三者提供）',
      item1: {
        intro:
          '当社は，次に掲げる場合を除いて，あらかじめユーザーの同意を得ることなく，第三者に個人情報を提供することはありません。ただし，個人情報保護法その他の法令で認められる場合を除きます。',
        sub: [
          '人の生命，身体または財産の保護のために必要がある場合であって，本人の同意を得ることが困難であるとき',
          '公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合であって，本人の同意を得ることが困難であるとき',
          '国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって，本人の同意を得ることにより当該事務の遂行に支障を及ぼすおそれがあるとき',
        ],
        lastSubIntro:
          '予め次の事項を告知あるいは公表し，かつ当社が個人情報保護委員会に届出をしたとき',
        lastSub: [
          '利用目的に第三者への提供を含むこと',
          '第三者に提供されるデータの項目',
          '第三者への提供の手段または方法',
          '本人の求めに応じて個人情報の第三者への提供を停止すること',
          '本人の求めを受け付ける方法',
        ],
      },
      item2: {
        intro:
          '前項の定めにかかわらず，次に掲げる場合には，当該情報の提供先は第三者に該当しないものとします。',
        sub: [
          '当社が利用目的の達成に必要な範囲内において個人情報の取扱いの全部または一部を委託する場合',
          '合併その他の事由による事業の承継に伴って個人情報が提供される場合',
          '個人情報を特定の者との間で共同して利用する場合であって，その旨並びに共同して利用される個人情報の項目，共同して利用する者の範囲，利用する者の利用目的および当該個人情報の管理について責任を有する者の氏名または名称について，あらかじめ本人に通知し，または本人が容易に知り得る状態に置いた場合',
        ],
      },
    },
    art6: {
      title: '第6条（個人情報の開示）',
      item1: {
        intro:
          '当社は，本人から個人情報の開示を求められたときは，本人に対し，遅滞なくこれを開示します。ただし，開示することにより次のいずれかに該当する場合は，その全部または一部を開示しないこともあり，開示しない決定をした場合には，その旨を遅滞なく通知します。なお，個人情報の開示に際しては，1件あたり1，000円の手数料を申し受けます。',
        sub: [
          '本人または第三者の生命，身体，財産その他の権利利益を害するおそれがある場合',
          '当社の業務の適正な実施に著しい支障を及ぼすおそれがある場合',
          'その他法令に違反することとなる場合',
        ],
      },
      item2:
        '前項の定めにかかわらず，履歴情報および特性情報などの個人情報以外の情報については，原則として開示いたしません。',
    },
    art7: {
      title: '第7条（個人情報の訂正および削除）',
      items: [
        'ユーザーは，当社の保有する自己の個人情報が誤った情報である場合には，当社が定める手続きにより，当社に対して個人情報の訂正，追加または削除（以下，「訂正等」といいます。）を請求することができます。',
        '当社は，ユーザーから前項の請求を受けてその請求に応じる必要があると判断した場合には，遅滞なく，当該個人情報の訂正等を行うものとします。',
        '当社は，前項の規定に基づき訂正等を行った場合，または訂正等を行わない旨の決定をしたときは遅滞なく，これをユーザーに通知します。',
      ],
    },
    art8: {
      title: '第8条（個人情報の利用停止等）',
      items: [
        '当社は，本人から，個人情報が，利用目的の範囲を超えて取り扱われているという理由，または不正の手段により取得されたものであるという理由により，その利用の停止または消去（以下，「利用停止等」といいます。）を求められた場合には，遅滞なく必要な調査を行います。',
        '前項の調査結果に基づき，その請求に応じる必要があると判断した場合には，遅滞なく，当該個人情報の利用停止等を行います。',
        '当社は，前項の規定に基づき利用停止等を行った場合，または利用停止等を行わない旨の決定をしたときは，遅滞なく，これをユーザーに通知します。',
        '前2項にかかわらず，利用停止等に多額の費用を有する場合その他利用停止等を行うことが困難な場合であって，ユーザーの権利利益を保護するために必要なこれに代わるべき措置をとれる場合は，この代替策を講じるものとします。',
      ],
    },
    art9: {
      title: '第9条（プライバシーポリシーの変更）',
      items: [
        '本ポリシーの内容は，法令その他本ポリシーに別段の定めのある事項を除いて，ユーザーに通知することなく，変更することができるものとします。',
        '当社が別途定める場合を除いて，変更後のプライバシーポリシーは，本ウェブサイトに掲載したときから効力を生じるものとします。',
      ],
    },
    art10: {
      title: '第10条（お問い合わせ窓口）',
      intro: '本ポリシーに関するお問い合わせは，下記の窓口までお願いいたします。',
      address1: '住所：〒222-0033 神奈川県横浜市港北区新横浜２丁目５−４',
      address2: '京浜第二ビル504',
      company: '社名：CJインベストメント株式会社',
      rep: '代表取締役：李 松',
      emailLabel: 'Eメールアドレス：',
    },
  },
  zh: {
    title: '隐私政策',
    lead: 'CJ投资株式会社（以下简称「本公司」）就本网站所提供服务（以下简称「本服务」）中用户个人信息的处理，特制定如下隐私政策（以下简称「本政策」）。',
    art1: {
      title: '第1条（个人信息）',
      body: '「个人信息」是指《个人信息保护法》所称的「个人信息」，即与生存的个人有关的信息，包括该信息所含的姓名、出生年月日、住址、电话号码、联系方式等记述可识别特定个人的信息，以及容貌、指纹、声纹相关数据，和健康保险证的保险者编号等仅凭该信息本身即可识别特定个人的信息（个人识别信息）。',
    },
    art2: {
      title: '第2条（个人信息的收集方法）',
      body: '本公司在用户进行使用登记时，可能会询问姓名、出生年月日、住址、电话号码、电子邮箱地址、银行账号、信用卡号、驾驶证号等个人信息。此外，本公司可能会从合作方（包括信息提供方、广告主、广告投放对象等，以下简称「合作方」）等处，收集用户与合作方之间产生的、包含用户个人信息的交易记录及结算相关信息。',
    },
    art3: {
      title: '第3条（收集・利用个人信息的目的）',
      intro: '本公司收集・利用个人信息的目的如下。',
      list: [
        '为提供・运营本公司服务',
        '为回复用户的咨询（包括进行本人确认）',
        '为发送用户正在使用的服务的新功能、更新信息、活动等，以及本公司提供的其他服务介绍的邮件',
        '为在必要时进行维护、重要通知等联络',
        '为识别违反使用条款的用户，或以不正当・不当目的使用服务的用户，并拒绝其使用',
        '为便于用户查阅、变更、删除自身的登记信息及查阅使用情况',
        '为在收费服务中向用户收取使用费用',
        '以及伴随上述利用目的的附带目的',
      ],
    },
    art4: {
      title: '第4条（利用目的的变更）',
      items: [
        '本公司仅在变更后的利用目的被合理认定与变更前具有关联性的情况下，方可变更个人信息的利用目的。',
        '在变更利用目的时，本公司将就变更后的目的，按照本公司规定的方法通知用户，或在本网站上予以公布。',
      ],
    },
    art5: {
      title: '第5条（个人信息向第三方提供）',
      item1: {
        intro:
          '除以下所列情形外，本公司不会在未事先取得用户同意的情况下向第三方提供个人信息。但《个人信息保护法》及其他法令所认可的情形除外。',
        sub: [
          '为保护人的生命、身体或财产之必要，且难以取得本人同意时',
          '为提升公共卫生或推进儿童健全成长之特别必要，且难以取得本人同意时',
          '在需要协助国家机关、地方公共团体或受其委托者执行法令所规定的事务，且因取得本人同意可能妨碍该事务执行时',
        ],
        lastSubIntro: '在事先告知或公布下列事项，且本公司已向个人信息保护委员会备案时',
        lastSub: [
          '利用目的中包含向第三方提供',
          '向第三方提供的数据项目',
          '向第三方提供的手段或方法',
          '应本人要求停止向第三方提供个人信息',
          '受理本人请求的方法',
        ],
      },
      item2: {
        intro:
          '尽管有前项规定，在下列情形下，该信息的提供对象不视为第三方。',
        sub: [
          '本公司在实现利用目的所必需的范围内，将个人信息处理的全部或一部分委托他人时',
          '因合并或其他事由导致业务承继而提供个人信息时',
          '与特定者共同利用个人信息，且就此意旨以及共同利用的个人信息项目、共同利用者的范围、利用者的利用目的，和对该个人信息管理负责者的姓名或名称，事先通知本人或置于本人易于知悉的状态时',
        ],
      },
    },
    art6: {
      title: '第6条（个人信息的披露）',
      item1: {
        intro:
          '当本人要求披露个人信息时，本公司将不迟延地向本人披露。但若因披露而符合下列任一情形，本公司可能不披露其全部或一部分；作出不予披露的决定时，将不迟延地通知本人。此外，每披露1件个人信息，本公司将收取1,000日元的手续费。',
        sub: [
          '可能损害本人或第三方的生命、身体、财产及其他权利利益时',
          '可能对本公司业务的正当实施造成重大妨碍时',
          '其他将构成违反法令的情形',
        ],
      },
      item2:
        '尽管有前项规定，对于历史信息及特性信息等个人信息以外的信息，原则上不予披露。',
    },
    art7: {
      title: '第7条（个人信息的更正及删除）',
      items: [
        '当本公司持有的用户本人个人信息存在错误时，用户可按照本公司规定的手续，向本公司请求更正、追加或删除个人信息（以下简称「更正等」）。',
        '本公司在收到用户前项请求并判断有必要响应该请求时，将不迟延地对该个人信息进行更正等处理。',
        '本公司在依前项规定进行更正等处理，或作出不予更正等的决定时，将不迟延地通知用户。',
      ],
    },
    art8: {
      title: '第8条（个人信息的停止利用等）',
      items: [
        '当本公司被本人以个人信息超出利用目的范围被处理，或系以不正当手段取得为由，要求停止利用或删除（以下简称「停止利用等」）时，将不迟延地进行必要的调查。',
        '根据前项调查结果，判断有必要响应该请求时，将不迟延地对该个人信息进行停止利用等处理。',
        '本公司在依前项规定进行停止利用等处理，或作出不予停止利用等的决定时，将不迟延地通知用户。',
        '尽管有前两项规定，在停止利用等需要高额费用或其他难以进行停止利用等的情形下，若能够采取为保护用户权利利益所必需的替代措施，本公司将采取该替代措施。',
      ],
    },
    art9: {
      title: '第9条（隐私政策的变更）',
      items: [
        '除法令及本政策另有规定的事项外，本政策的内容可在不通知用户的情况下进行变更。',
        '除本公司另行规定的情形外，变更后的隐私政策自在本网站上公布之时起生效。',
      ],
    },
    art10: {
      title: '第10条（咨询窗口）',
      intro: '有关本政策的咨询，请联系下列窗口。',
      address1: '地址：〒222-0033 神奈川县横滨市港北区新横滨2丁目5−4',
      address2: '京浜第二大厦504',
      company: '公司名称：CJ投资株式会社',
      rep: '代表董事：李 松',
      emailLabel: '电子邮箱：',
    },
  },
};
